import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DbTranslationService } from './db-translation.service';
import { LanguageService } from './language.service';

/**
 * Keeps Google Translate (cookie + widget) and DB-driven custom translations aligned
 * across SPA navigations. Without this, cookie/localStorage/DbTranslationService can drift,
 * causing empty notranslate spans, mixed EN/HI, or stale API language headers.
 */
@Injectable({ providedIn: 'root' })
export class TranslationSyncService {
  constructor(
    private router: Router,
    private language: LanguageService,
    private dbt: DbTranslationService,
    private ngZone: NgZone,
    private appRef: ApplicationRef
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        // Let the routed component render first, then sync (reduces empty-node races with GT).
        setTimeout(() => void this.syncAfterNavigation(), 0);
      });
  }

  private async syncAfterNavigation(): Promise<void> {
    const lang = this.language.syncFromBrowser();
    await this.dbt.ensureAligned(lang);
    if (lang === 'hi') {
      // Full EN → HI cycle so each route behaves like a fresh translation pass.
      await this.language.hardRefreshHindiTranslation();
    }
    this.ngZone.run(() => this.appRef.tick());
  }
}
